import {
  CheckSquare,
  FileText,
  Link as LinkIcon,
  Play,
  ShoppingCart,
  Package,
  BookOpen,
  Bell,
  ListFilter,
  Film,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { useNoteTypesQuery } from "@/mutations/noteTypes";
import { cn } from "@/lib/utils";

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  CheckSquare,
  FileText,
  Link: LinkIcon,
  Play,
  Film,
  ShoppingCart,
  Package,
  BookOpen,
  Bell,
};

interface OverdueFilterDropdownProps {
  excludedTypes: string[];
  onToggleType: (typeName: string) => void;
  isTypeIncluded: (typeName: string) => boolean;
  hasActiveFilters: boolean;
  allIncluded?: boolean;
  onIncludeAll?: () => void;
  onExcludeAll?: (allTypes: string[]) => void;
}

export function OverdueFilterDropdown({
  onToggleType,
  isTypeIncluded,
  hasActiveFilters,
  allIncluded,
  onIncludeAll,
  onExcludeAll,
}: OverdueFilterDropdownProps) {
  const { data: noteTypes = [] } = useNoteTypesQuery();

  const handleAllChange = () => {
    if (allIncluded) {
      onExcludeAll?.(noteTypes.map((t) => t.name));
    } else {
      onIncludeAll?.();
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 gap-1.5 text-muted-foreground hover:text-foreground"
        >
          <div className="relative">
            <ListFilter className="h-4 w-4" />
            {hasActiveFilters && (
              <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-primary" />
            )}
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-3">
        <div className="space-y-3">
          <div className="space-y-2">
            {onIncludeAll && onExcludeAll && (
              <>
                <label
                  className={cn(
                    "flex items-center gap-2.5 py-1.5 px-2 rounded-md cursor-pointer transition-colors",
                    "hover:bg-muted/50 font-medium",
                  )}
                >
                  <Checkbox
                    checked={allIncluded}
                    onCheckedChange={handleAllChange}
                    className="h-4 w-4"
                  />
                  <span className="text-sm">All</span>
                </label>
                <div className="border-b border-border my-2" />
              </>
            )}
            {noteTypes.map((type) => {
              const Icon = type.icon ? iconMap[type.icon] : FileText;
              const isIncluded = isTypeIncluded(type.name);

              return (
                <label
                  key={type.id}
                  className={cn(
                    "flex items-center gap-2.5 py-1.5 px-2 rounded-md cursor-pointer transition-colors",
                    "hover:bg-muted/50",
                    !isIncluded && "opacity-60",
                  )}
                >
                  <Checkbox
                    checked={isIncluded}
                    onCheckedChange={() => onToggleType(type.name)}
                    className="h-4 w-4"
                  />
                  <div
                    className="flex items-center gap-2 flex-1 min-w-0"
                    style={{ color: type.color || undefined }}
                  >
                    {Icon && <Icon className="h-3.5 w-3.5 flex-shrink-0" />}
                    <span className="text-sm truncate text-foreground">
                      {type.label}
                    </span>
                  </div>
                </label>
              );
            })}
          </div>
          {noteTypes.length === 0 && (
            <div className="text-xs text-muted-foreground py-2 text-center">
              Loading types...
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
